import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { cn } from "../../lib/utils";

interface TypingEffectProps {
  texts?: string[];
  className?: string;
  rotationInterval?: number;
  typingSpeed?: number;
}

export const TypingEffect = ({
  texts = ["SAP", "Oracle", "NetSuite"],
  className,
  rotationInterval = 2200,
  typingSpeed = 90,
}: TypingEffectProps) => {
  const [displayedText, setDisplayedText] = useState("");
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: true });

  const currentText = texts[currentTextIndex % texts.length];

  useEffect(() => {
    if (!isInView) return;

    if (charIndex < currentText.length) {
      const t = setTimeout(() => {
        setDisplayedText((prev) => prev + currentText.charAt(charIndex));
        setCharIndex(charIndex + 1);
      }, typingSpeed);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => {
        setDisplayedText("");
        setCharIndex(0);
        setCurrentTextIndex((prev) => (prev + 1) % texts.length);
      }, rotationInterval);
      return () => clearTimeout(t);
    }
  }, [charIndex, currentText, isInView, typingSpeed, rotationInterval, texts.length]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative inline-flex items-center justify-center text-center font-bold",
        className
      )}
    >
      <span>{displayedText}</span>
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
        className="ml-1 h-[0.8em] w-[3px] rounded-sm bg-current inline-block align-middle"
      />
    </div>
  );
};

export default TypingEffect;
